import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';
import { auctionAlertService } from '../../../services/auctionAlertService';

const FREQUENCY_OPTIONS = [
  { value: 'immediate', label: 'Immediately', description: 'Get alerted right away' },
  { value: 'hourly', label: 'Max once/hour', description: 'Grouped hourly' },
  { value: 'daily', label: 'Max once/day', description: 'Daily digest' },
  { value: 'disabled', label: 'Disabled', description: 'No alerts' }
];

const HOURS_OPTIONS = [
  { value: 1, label: '1 hour before end' },
  { value: 2, label: '2 hours before end' },
  { value: 4, label: '4 hours before end' },
  { value: 6, label: '6 hours before end' },
  { value: 12, label: '12 hours before end' },
  { value: 24, label: '24 hours before end' }
];

const AlertRow = ({ icon, iconColor, title, description, enabled, frequency, onToggle, onFrequencyChange, showFrequency = true }) => (
  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-4 bg-muted/20 rounded-lg border border-border/50">
    <div className="flex items-start gap-3">
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconColor}`}>
        <Icon name={icon} size={18} />
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{title}</p>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </div>
    </div>
    <div className="flex items-center gap-3 ml-12 sm:ml-0">
      {showFrequency && enabled && (
        <select
          value={frequency}
          onChange={(e) => onFrequencyChange(e?.target?.value)}
          className="text-xs bg-background border border-border rounded-md px-2 py-1.5 text-foreground focus:outline-none focus:ring-1 focus:ring-primary"
        >
          {FREQUENCY_OPTIONS?.map(opt => (
            <option key={opt?.value} value={opt?.value}>{opt?.label}</option>
          ))}
        </select>
      )}
      <button
        onClick={onToggle}
        className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none flex-shrink-0 ${
          enabled ? 'bg-primary' : 'bg-muted'
        }`}
      >
        <span
          className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform duration-200 ${
            enabled ? 'translate-x-4' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  </div>
);

const AuctionAlertSettings = ({ onClose }) => {
  const { user, userProfile } = useAuth();
  const [prefs, setPrefs] = useState({
    final_hours_alert: true,
    outbid_alert: true,
    bid_changed_alert: true,
    auction_won_alert: true,
    auction_ended_alert: false,
    final_hours_frequency: 'immediate',
    outbid_frequency: 'immediate',
    bid_changed_frequency: 'hourly',
    final_hours_threshold: 2,
    sms_enabled: true,
    push_enabled: true
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState('settings');

  const hasPhone = userProfile?.phone_number && userProfile?.phone_verified;

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
      loadRecentAlerts();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    setLoading(true);
    const { data } = await auctionAlertService?.getAlertPreferences(user?.id);
    if (data) {
      setPrefs(prev => ({ ...prev, ...data }));
    }
    setLoading(false);
  };

  const loadRecentAlerts = async () => {
    const { data } = await auctionAlertService?.getAlertLogs(user?.id, 10);
    setRecentAlerts(data || []);
  };

  const handleToggle = (key) => {
    setPrefs(prev => ({ ...prev, [key]: !prev?.[key] }));
    setSaved(false);
  };

  const handleFrequencyChange = (key, value) => {
    setPrefs(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    const { error } = await auctionAlertService?.updateAlertPreferences(user?.id, {
      final_hours_alert: prefs?.final_hours_alert,
      outbid_alert: prefs?.outbid_alert,
      bid_changed_alert: prefs?.bid_changed_alert,
      auction_won_alert: prefs?.auction_won_alert,
      auction_ended_alert: prefs?.auction_ended_alert,
      final_hours_frequency: prefs?.final_hours_frequency,
      outbid_frequency: prefs?.outbid_frequency,
      bid_changed_frequency: prefs?.bid_changed_frequency,
      final_hours_threshold: prefs?.final_hours_threshold,
      sms_enabled: prefs?.sms_enabled,
      push_enabled: prefs?.push_enabled
    });
    setSaving(false);
    if (!error) {
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    }
  };

  const getAlertTypeLabel = (type) => {
    const labels = {
      final_hours_alert: 'Final Hours',
      outbid_alert: 'Outbid',
      bid_changed_alert: 'Bid Changed',
      auction_won_alert: 'Auction Won',
      auction_ended_alert: 'Auction Ended'
    };
    return labels?.[type] || type;
  };

  const getChannelIcon = (channel) => channel === 'sms' ? 'MessageSquare' : 'Bell';

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date?.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-border bg-muted/20">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Bell" size={18} className="text-primary" />
          </div>
          <div>
            <h3 className="text-base font-semibold text-foreground">Auction Alerts</h3>
            <p className="text-xs text-muted-foreground">SMS & push notification settings</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onClose && (
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-muted transition-colors"
            >
              <Icon name="X" size={16} className="text-muted-foreground" />
            </button>
          )}
        </div>
      </div>
      {/* Tabs */}
      <div className="flex border-b border-border">
        {['settings', 'history']?.map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 py-3 text-sm font-medium capitalize transition-colors ${
              activeTab === tab
                ? 'text-primary border-b-2 border-primary bg-primary/5' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab === 'settings' ? 'Alert Settings' : 'Alert History'}
          </button>
        ))}
      </div>
      <div className="p-6">
        {activeTab === 'settings' ? (
          <div className="space-y-6">
            {/* Delivery Channels */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Send" size={14} className="text-muted-foreground" />
                Delivery Channels
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className={`flex items-center justify-between p-3 rounded-lg border ${prefs?.push_enabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/10'}`}>
                  <div className="flex items-center gap-2">
                    <Icon name="Bell" size={16} className={prefs?.push_enabled ? 'text-primary' : 'text-muted-foreground'} />
                    <div>
                      <p className="text-sm font-medium text-foreground">Push Alerts</p>
                      <p className="text-xs text-muted-foreground">In-app notifications</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggle('push_enabled')}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${prefs?.push_enabled ? 'bg-primary' : 'bg-muted'}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${prefs?.push_enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>

                <div className={`flex items-center justify-between p-3 rounded-lg border ${prefs?.sms_enabled ? 'border-primary/30 bg-primary/5' : 'border-border bg-muted/10'}`}>
                  <div className="flex items-center gap-2">
                    <Icon name="MessageSquare" size={16} className={prefs?.sms_enabled ? 'text-primary' : 'text-muted-foreground'} />
                    <div>
                      <p className="text-sm font-medium text-foreground">SMS Alerts</p>
                      <p className="text-xs text-muted-foreground">
                        {hasPhone ? 'Text messages' : 'Add phone in profile'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggle('sms_enabled')}
                    disabled={!hasPhone}
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${prefs?.sms_enabled && hasPhone ? 'bg-primary' : 'bg-muted'} ${!hasPhone ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${prefs?.sms_enabled && hasPhone ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </button>
                </div>
              </div>
              {!hasPhone && (
                <p className="text-xs text-warning mt-2 flex items-center gap-1">
                  <Icon name="AlertCircle" size={12} />
                  Add and verify your phone number in Profile Settings to enable SMS alerts
                </p>
              )}
            </div>

            {/* Alert Types */}
            <div>
              <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="Zap" size={14} className="text-muted-foreground" />
                Alert Types & Frequency
              </h4>
              <div className="space-y-3">
                <AlertRow
                  icon="Clock"
                  iconColor="bg-warning/10 text-warning"
                  title="Final Hours Warning"
                  description={`Alert when auction enters final ${prefs?.final_hours_threshold}h`}
                  enabled={prefs?.final_hours_alert}
                  frequency={prefs?.final_hours_frequency}
                  onToggle={() => handleToggle('final_hours_alert')}
                  onFrequencyChange={(v) => handleFrequencyChange('final_hours_frequency', v)}
                />

                <AlertRow
                  icon="TrendingUp"
                  iconColor="bg-destructive/10 text-destructive"
                  title="Outbid Alert"
                  description="Notify when someone outbids you"
                  enabled={prefs?.outbid_alert}
                  frequency={prefs?.outbid_frequency}
                  onToggle={() => handleToggle('outbid_alert')}
                  onFrequencyChange={(v) => handleFrequencyChange('outbid_frequency', v)}
                />

                <AlertRow
                  icon="Activity"
                  iconColor="bg-blue-500/10 text-blue-500"
                  title="Highest Bid Changed"
                  description="Alert when the top bid changes on watched auctions"
                  enabled={prefs?.bid_changed_alert}
                  frequency={prefs?.bid_changed_frequency}
                  onToggle={() => handleToggle('bid_changed_alert')}
                  onFrequencyChange={(v) => handleFrequencyChange('bid_changed_frequency', v)}
                />

                <AlertRow
                  icon="Trophy"
                  iconColor="bg-success/10 text-success"
                  title="Auction Won"
                  description="Celebrate when you win an auction"
                  enabled={prefs?.auction_won_alert}
                  frequency={null}
                  onToggle={() => handleToggle('auction_won_alert')}
                  showFrequency={false}
                />

                <AlertRow
                  icon="Flag"
                  iconColor="bg-muted text-muted-foreground"
                  title="Auction Ended"
                  description="Notify when a watched auction concludes"
                  enabled={prefs?.auction_ended_alert}
                  frequency={null}
                  onToggle={() => handleToggle('auction_ended_alert')}
                  showFrequency={false}
                />
              </div>
            </div>

            {/* Final Hours Threshold */}
            {prefs?.final_hours_alert && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                  <Icon name="Timer" size={14} className="text-muted-foreground" />
                  Final Hours Threshold
                </h4>
                <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                  {HOURS_OPTIONS?.map(opt => (
                    <button
                      key={opt?.value}
                      onClick={() => handleFrequencyChange('final_hours_threshold', opt?.value)}
                      className={`py-2 px-1 text-xs rounded-lg border text-center transition-colors ${
                        prefs?.final_hours_threshold === opt?.value
                          ? 'border-primary bg-primary/10 text-primary font-medium' :'border-border bg-muted/20 text-muted-foreground hover:border-primary/50'
                      }`}
                    >
                      {opt?.value}h
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  Trigger final hours alert when {prefs?.final_hours_threshold} hour{prefs?.final_hours_threshold !== 1 ? 's' : ''} remain
                </p>
              </div>
            )}

            {/* Save Button */}
            <div className="flex items-center justify-between pt-2 border-t border-border">
              <p className="text-xs text-muted-foreground">
                Changes apply to all future auction alerts
              </p>
              <button
                onClick={handleSave}
                disabled={saving}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  saved
                    ? 'bg-success/10 text-success border border-success/30' :'bg-primary text-primary-foreground hover:bg-primary/90'
                } disabled:opacity-60`}
              >
                {saving ? (
                  <><div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-current" />Saving...</>
                ) : saved ? (
                  <><Icon name="Check" size={14} />Saved!</>
                ) : (
                  <><Icon name="Save" size={14} />Save Settings</>
                )}
              </button>
            </div>
          </div>
        ) : (
          /* Alert History Tab */
          <div className="space-y-3">
            {recentAlerts?.length === 0 ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 bg-muted/30 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Icon name="BellOff" size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No alerts sent yet</p>
                <p className="text-xs text-muted-foreground mt-1">Alerts will appear here once triggered</p>
              </div>
            ) : (
              recentAlerts?.map(alert => (
                <div key={alert?.id} className="flex items-start gap-3 p-3 bg-muted/20 rounded-lg">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                    alert?.status === 'sent' ? 'bg-success/10' : 'bg-destructive/10'
                  }`}>
                    <Icon
                      name={getChannelIcon(alert?.channel)}
                      size={13}
                      className={alert?.status === 'sent' ? 'text-success' : 'text-destructive'}
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-foreground">
                        {getAlertTypeLabel(alert?.alert_type)}
                      </span>
                      <span className="text-xs text-muted-foreground flex-shrink-0">
                        {formatTime(alert?.sent_at)}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{alert?.message}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        alert?.channel === 'sms' ? 'bg-blue-500/10 text-blue-500' : 'bg-primary/10 text-primary'
                      }`}>
                        {alert?.channel?.toUpperCase()}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        alert?.status === 'sent' ? 'bg-success/10 text-success' : 'bg-destructive/10 text-destructive'
                      }`}>
                        {alert?.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuctionAlertSettings;
